import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { RootState } from './store';
import {
    setResponsibilityCenterCache,
    addResponsibilityCenter,
    setLoading,
    setError,
    clearResponsibilityCenterCache
} from './responsibilityCenterReducer';

/**
 * Hook to get the responsibility center cache as a Map
 */
export const useResponsibilityCenterCache = () => {
    const cacheArray = useSelector((state: RootState) => state.responsibilityCenter.cache);
    const loading = useSelector((state: RootState) => state.responsibilityCenter.loading);
    const error = useSelector((state: RootState) => state.responsibilityCenter.error);
    const isLoaded = useSelector((state: RootState) => state.responsibilityCenter.isLoaded);

    // Convert array of tuples back to Map for easier use
    const cache = useMemo(() => {
        return new Map<string, string[]>(cacheArray);
    }, [cacheArray]);

    return { cache, loading, error, isLoaded };
};

/**
 * Hook to get actions for managing responsibility center cache
 */
export const useResponsibilityCenterActions = () => {
    const dispatch = useDispatch();

    const setCache = useCallback((cache: Map<string, string[]>) => {
        // Convert Map to array of tuples for serialization before dispatching
        const cacheArray = Array.from(cache.entries());
        dispatch(setResponsibilityCenterCache(cacheArray));
    }, [dispatch]);

    const addRC = useCallback((rcId: string, ids: string[]) => {
        dispatch(addResponsibilityCenter({ rcId, ids }));
    }, [dispatch]);

    const setLoadingState = useCallback((loading: boolean) => {
        dispatch(setLoading(loading));
    }, [dispatch]);

    const setErrorState = useCallback((error: string | null) => {
        dispatch(setError(error));
    }, [dispatch]);

    const clearCache = useCallback(() => {
        dispatch(clearResponsibilityCenterCache());
    }, [dispatch]);

    return {
        setCache,
        addRC,
        setLoadingState,
        setErrorState,
        clearCache
    };
};

